/**
 * High-level hooks that combine React Query data fetching with Context state
 * This is the main interface for components to access idea page functionality
 */
import { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth0 } from '@auth0/auth0-react';
import { useIdeaPageContext } from '../../components/ideaNiv/IdeaPageContext';
import {
  useIdeaDetail,
  // useFilteredEvidences, // Commented out - API response not being used
  useAssignedEvidencesInfinite,
  useSuggestedEvidencesInfinite,
  useAdditionalEvidences,
  useIdeaTabs as useIdeaTabsQuery,
  useTabEvidenceTotals,
  useIdeaThemes,
  useSubIdeas,
  useUpdateIdea,
  useToggleIdeaStar,
  usePerspectiveConfigurations,
  useImpactAggregationProperties,
  useIdeaInfoTabsConfig,
  useIdeaColumnsConfig,
  useSubPagesConfig,
  useIdeaActivityLogs,
  useProductStatusHistory,
  extractEvidenceIdsForMLApi,
} from './useIdeaQueries';
import { ideaKeys } from './ideaKeys';
import { ActiveFilter } from '../useFilters';
import {
  combineFilters,
  cleanEmptyFilters,
  buildPerspectiveFiltersCacheKey,
  doesCheckboxInFilterMatch,
} from './filterHelpers';
import { getCustomTimeRangeCount } from '../../api/idea';
import { getSubThemes } from '../../api/idea';
import {evidenceInclusionToState, stateToEvidenceInclusion} from "../useEvidenceInclusion";
import {
  isEvidenceSectionLoading,
  isSuggestedSection,
  resolveSectionCountFromTabTotals,
} from './evidenceSectionHelpers';
import {
  getClientSectionFiltersToApply,
  resolveSuggestedApiSectionFilters,
} from './tabSectionFilterHelpers';

/** Shared across all useIdeaPage() instances — prevents the ~50 concurrent hook
 * calls from each triggering fetchNextPage on the same first-page result. */
const _suggestedPrefetchedPage1 = new Map<string, unknown>();
const _assignedPrefetchedPage1 = new Map<string, unknown>();

interface IdeaState {
  _id: string;
  title: string;
  description: string;
  state: "active" | "archived" | "generated";
  status: string;
  priority: string;
  domain: string;
  componentId: string;
  ownerId: string;
  ownerName: string;
  tags: string[];
  score: number;
  createdAt: Date;
  updatedAt: Date;
  starred: string[];
  productItem?: {
    _id: string;
    name: string;
    type: string;
  };
  componentObj?: {
    _id: string;
    name: string;
  };
  duplicateIdea?: {
    _id: string;
    title: string;
    score: number;
  };
  duplicationType?: string;
  duplicationId?: string;
  /** Set when this idea is a child of another; used to hide "Create idea from theme" on child ideas */
  parentIdeaId?: string;
}

// Utility function to convert activeFilters array to string for API
const convertActiveFiltersToString = (filters: ActiveFilter[]): string => {
  if (!filters || filters.length === 0) {
    return '';
  }

  let resultString = '';

  filters.forEach((doc, index) => {
    let filterString = '';
    if (doc.name === '_id') {return;}
    const filterType = doc.type as string;

    if (filterType === 'checkbox') {
      if (doc.selected && Array.isArray(doc.selected)) {
        filterString = `${doc.name}_${doc.selected.join(',')}`;
      }
    } else if (filterType === 'date') {
      if (doc.selected) {
        if (Array.isArray(doc.selected) && doc.selected.length === 2) {
          // Date range format
          const startDate = new Date(doc.selected[0]).toISOString().split('T')[0];
          const endDate = new Date(doc.selected[1]).toISOString().split('T')[0];
          filterString = `${doc.name}_${startDate}_${endDate}`;
        } else if (typeof doc.selected === 'string') {
          // Operation format (thisWeek, lastMonth, etc.)
          if (doc.selected.trim() !== '') {
            filterString = `${doc.name}_${doc.selected}`;
          }
        }
      }
    } else if (filterType === 'fetchableCheckbox') {
      if (doc.selected && Array.isArray(doc.selected)) {
        filterString = `${doc.name}_${doc.selected.join(',')}`;
      }
    } else if (filterType === 'boolean') {
      if (doc.selected !== undefined && doc.selected !== null) {
        filterString = `${doc.name}_${doc.selected}`;
      }
    } else if (filterType === 'number') {
      if (doc.selected !== undefined && doc.selected !== null && !Array.isArray(doc.selected)) {
        if (typeof doc.selected === 'string' && doc.selected !== '') {
          filterString = `${doc.name}_${doc.selected}`;
        } else if (typeof doc.selected === 'number') {
          filterString = `${doc.name}_${doc.selected}`;
        }
      }
    } else if (filterType === 'parent') {
      // Parent filters are containers, skip them
      return;
    } else if (filterType === 'userIdIn') {
      if (doc.selected !== undefined && doc.selected !== null) {
        filterString = `${doc.name}_${doc.operator}`;
      }
    } else {
      // Handle unknown types or fallback
      if (doc.selected) {
        if (Array.isArray(doc.selected)) {
          filterString = `${doc.name}_${doc.selected.join(',')}`;
        } else {
          filterString = `${doc.name}_${doc.selected}`;
        }
      }
    }

    // Add to result string with separator
    if (filterString) {
      if (resultString) {
        resultString += '|';
      }
      resultString += filterString;
    }
  });

  return resultString;
};

// Main hook for idea page data and state
// One entry per ideaId while a recalculate is in progress.
// Shared across all 50+ useIdeaPage instances so only the first one fires.
const _activeRecalcIds = new Set<string>();
export const useIdeaPage = (): any => {
  
  const { id: ideaId } = useParams<{ id: string }>();
  const context = useIdeaPageContext();
  const [comments, setComments] = useState<any[]>([]);
  
  // const [shouldCallMLThemes, setShouldCallMLThemes] = useState(false);
  // Extract state and actions from context
  const {
    activeTab,
    setActiveTab,
    searchQuery,
    debouncedSearchQuery,
    sortConfig,
    activeFilters,
    setActiveFilters,
    trendFilters,
    setTrendFilters,
    selectedEvidenceIds,
    filteredThemeEvidenceIds,
    setFilteredThemeEvidenceIds,
    isSearching,
    themeEvidenceIds,
    recalculateThemes,
    setRecalculateThemes,
    setIsThemeFound,
    unreadNotificationsMap,
    evidenceInclusion,
    setUnreadNotificationsMap
    // ... all other context state/actions
  } = context;

  const isThemeFilterActive = Array.isArray(activeFilters) && activeFilters.some((filter: any) => filter.name === '_id');

  // Derive combined evidence IDs from all _id (theme) filters so multiple themes can be applied
  const themeEvidenceIdsFromFilters = useMemo(() => {
    if (!Array.isArray(activeFilters)) return [];
    const idFilters = activeFilters.filter((f: any) => f.name === '_id' && Array.isArray(f.selected));
    if (idFilters.length === 0) return [];
    const combined = idFilters.flatMap((f: any) => f.selected);
    return [...new Set(combined)];
  }, [activeFilters]);
  const effectiveThemeEvidenceIds =
    themeEvidenceIdsFromFilters.length > 0 ? themeEvidenceIdsFromFilters : (themeEvidenceIds || []);

  // React Query hooks for server data
  const queryClient = useQueryClient();
  const ideaQuery = useIdeaDetail(ideaId);
  const tabsQuery = useIdeaTabsQuery();
  const tabs = tabsQuery.data || [];

  const suggestedSectionFiltersContext = useMemo(
    () => resolveSuggestedApiSectionFilters(tabs, activeTab, activeFilters, trendFilters),
    [tabs, activeTab, activeFilters, trendFilters]
  );
  
  const suggestedApiFilters = useMemo(
    () =>
      cleanEmptyFilters([
        ...(suggestedSectionFiltersContext.mergedActive || []),
        ...(suggestedSectionFiltersContext.mergedTrend || []),
      ]),
    [suggestedSectionFiltersContext.mergedActive, suggestedSectionFiltersContext.mergedTrend]
  );
  
  // Configuration hooks - replacing SyncContext
  const perspectiveConfigQuery = usePerspectiveConfigurations();
  const impactPropertiesQuery = useImpactAggregationProperties();
  const infoTabsConfigQuery = useIdeaInfoTabsConfig();
  const columnsConfigQuery = useIdeaColumnsConfig();
  const subPagesConfigQuery = useSubPagesConfig();
  
  // Separate queries for each data source
  const isTotalEvidenceInclusion = evidenceInclusion === 'total';

  const assignedEvidencesQuery = useAssignedEvidencesInfinite(
    ideaId,
    activeFilters,
    trendFilters,
    sortConfig.field && sortConfig.direction ? { field: sortConfig.field, direction: sortConfig.direction } : undefined,
    evidenceInclusion
  );

  const assignedEvidencesFlat = useMemo(() => {
    if (evidenceInclusion === 'suggested') {
      return [];
    }
    return assignedEvidencesQuery.data?.pages?.flatMap((page) => page?.evidences || []) || [];
  }, [assignedEvidencesQuery.data?.pages, evidenceInclusion]);

  const assignedFirstPageMeta = assignedEvidencesQuery.data?.pages?.[0];

  const assignedTotalCount = useMemo(() => {
    const firstPage = assignedEvidencesQuery.data?.pages?.[0];
    if (firstPage?.totalCount != null) {
      return firstPage.totalCount;
    }
    return assignedEvidencesFlat.length;
  }, [assignedEvidencesQuery.data?.pages, assignedEvidencesFlat.length]);

  const hasMoreAssigned = useMemo(() => {
    const pages = assignedEvidencesQuery.data?.pages;
    if (!pages?.length) {
      return false;
    }
    const lastPage = pages[pages.length - 1];
    if (lastPage?.hasMore != null) {
      return lastPage.hasMore;
    }
    return assignedEvidencesFlat.length < assignedTotalCount;
  }, [assignedEvidencesQuery.data?.pages, assignedEvidencesFlat.length, assignedTotalCount]);

  const fetchNextAssignedPageRef = useRef(assignedEvidencesQuery.fetchNextPage);
  fetchNextAssignedPageRef.current = assignedEvidencesQuery.fetchNextPage;

  const assignedActiveFiltersKey = JSON.stringify(activeFilters);
  const assignedTrendFiltersKey = JSON.stringify(trendFilters);
  const assignedSortKey = `${sortConfig.field}:${sortConfig.direction}`;

  useEffect(() => {
    _assignedPrefetchedPage1.delete(ideaId);
  }, [ideaId, assignedActiveFiltersKey, assignedTrendFiltersKey, assignedSortKey, evidenceInclusion]);

  useEffect(() => {
    if (evidenceInclusion === 'suggested' || !ideaId) return;
    if (!assignedEvidencesQuery.isSuccess) return;
    if (!hasMoreAssigned) return;
    if (assignedEvidencesQuery.isFetchingNextPage) return;
    if (!assignedEvidencesQuery.hasNextPage) return;

    const pages = assignedEvidencesQuery.data?.pages;
    const pageCount = pages?.length ?? 0;

    if (pageCount === 0) {
      _assignedPrefetchedPage1.delete(ideaId);
      return;
    }

    const loadedCount = assignedEvidencesFlat.length;
    const progressKey = `${pageCount}:${loadedCount}`;

    if (_assignedPrefetchedPage1.get(ideaId) === progressKey) return;

    _assignedPrefetchedPage1.set(ideaId, progressKey);
    void fetchNextAssignedPageRef.current().catch(() => {
      _assignedPrefetchedPage1.delete(ideaId);
    });
  }, [
    evidenceInclusion,
    ideaId,
    assignedEvidencesQuery.isSuccess,
    assignedEvidencesQuery.data?.pages,
    assignedEvidencesFlat.length,
    hasMoreAssigned,
    assignedEvidencesQuery.isFetchingNextPage,
    assignedEvidencesQuery.hasNextPage,
  ]);
  
  const suggestedEvidencesQuery = useSuggestedEvidencesInfinite(
    ideaId,
    suggestedSectionFiltersContext.mergedActive,
    suggestedSectionFiltersContext.mergedTrend,
    sortConfig.field && sortConfig.direction ? { field: sortConfig.field, direction: sortConfig.direction } : undefined,
    evidenceInclusion,
    {
      sectionFilters: suggestedSectionFiltersContext.apiMergeEnabled
        ? suggestedSectionFiltersContext.sectionFilters
        : undefined,
      activeTab: activeTab || undefined,
      hasEmptyFilterIntersection: suggestedSectionFiltersContext.hasEmptyFilterIntersection,
    }
  );

  const suggestedEvidencesFlat = useMemo(() => {
    if (!isTotalEvidenceInclusion) {
      return [];
    }
    return suggestedEvidencesQuery.data?.pages?.flatMap((page) => page?.evidences || []) || [];
  }, [suggestedEvidencesQuery.data?.pages, isTotalEvidenceInclusion]);

  const suggestedTotalCount = useMemo(() => {
    const firstPage = suggestedEvidencesQuery.data?.pages?.[0];
    if (firstPage?.totalCount != null) {
      return firstPage.totalCount;
    }
    return suggestedEvidencesFlat.length;
  }, [suggestedEvidencesQuery.data?.pages, suggestedEvidencesFlat.length]);

  const hasMoreSuggested = useMemo(() => {
    const pages = suggestedEvidencesQuery.data?.pages;
    if (!pages?.length) {
      return false;
    }
    const lastPage = pages[pages.length - 1];
    if (lastPage?.hasMore != null) {
      return lastPage.hasMore;
    }
    return suggestedEvidencesFlat.length < suggestedTotalCount;
  }, [suggestedEvidencesQuery.data?.pages, suggestedEvidencesFlat.length, suggestedTotalCount]);

  const suggestedFirstPageMeta = suggestedEvidencesQuery.data?.pages?.[0];

  // Stable ref to fetchNextPage — not listed as an effect dep.
  const fetchNextPageRef = useRef(suggestedEvidencesQuery.fetchNextPage);
  fetchNextPageRef.current = suggestedEvidencesQuery.fetchNextPage;

  // Reset the prefetch guard whenever the suggested-evidences query variables change
  // (ideaId, filters, sort, or tab). Without this, a stale progressKey from a previous
  // query can block fetches for a new query that happens to start at the same page count.
  const suggestedActiveFiltersKey = JSON.stringify(suggestedSectionFiltersContext.mergedActive);
  const suggestedTrendFiltersKey  = JSON.stringify(suggestedSectionFiltersContext.mergedTrend);
  const suggestedSortKey          = `${sortConfig.field}:${sortConfig.direction}`;
  const suggestedTabKey           = activeTab || '';
  useEffect(() => {
    _suggestedPrefetchedPage1.delete(ideaId);
  }, [ideaId, suggestedActiveFiltersKey, suggestedTrendFiltersKey, suggestedSortKey, suggestedTabKey, evidenceInclusion]);

  // useIdeaPage() is called by ~50 components simultaneously on the idea page.
  // A module-level Map guards fetchNextPage so only ONE instance fires per page.
  // This effect auto-fetches ALL remaining pages when hasMore is true, so the
  // full evidence set is available for themes (ML API) regardless of filters.
  useEffect(() => {
    if (!isTotalEvidenceInclusion || !ideaId) return;
    if (!suggestedEvidencesQuery.isSuccess) return;
    if (!hasMoreSuggested) return;
    if (suggestedEvidencesQuery.isFetchingNextPage) return;
    // Only proceed when React Query itself has a next page cursor — prevents
    // setting the guard on a no-op fetchNextPage that would stall pagination.
    if (!suggestedEvidencesQuery.hasNextPage) return;

    const pages = suggestedEvidencesQuery.data?.pages;
    const pageCount = pages?.length ?? 0;

    // Clear the shared guard when the query resets (0 pages = new query started).
    if (pageCount === 0) {
      _suggestedPrefetchedPage1.delete(ideaId);
      return;
    }

    // Build a key representing the current last page to detect progress.
    // Using pageCount + loadedCount ensures each new page advances the key.
    const loadedCount = suggestedEvidencesFlat.length;
    const progressKey = `${pageCount}:${loadedCount}`;

    // Already triggered a fetch for this exact progress point — skip.
    if (_suggestedPrefetchedPage1.get(ideaId) === progressKey) return;

    _suggestedPrefetchedPage1.set(ideaId, progressKey);
    void fetchNextPageRef.current().catch(() => {
      // Clear the guard on failure so the next effect run can retry.
      _suggestedPrefetchedPage1.delete(ideaId);
    });
  }, [
    isTotalEvidenceInclusion,
    ideaId,
    suggestedEvidencesQuery.isSuccess,
    suggestedEvidencesQuery.data?.pages,
    suggestedEvidencesFlat.length,
    hasMoreSuggested,
    suggestedEvidencesQuery.isFetchingNextPage,
    suggestedEvidencesQuery.hasNextPage,
  ]);

  const additionalEvidencesQuery = useAdditionalEvidences(
    ideaId,
    activeFilters,
    trendFilters,
    debouncedSearchQuery || undefined,
    sortConfig.field && sortConfig.direction ? { field: sortConfig.field, direction: sortConfig.direction } : undefined,
    evidenceInclusion
  );

  // Combine data from all queries for backward compatibility
  // Return empty arrays for suggested and additional when evidenceInclusion === 'assigned'
  const allEvidencesQuery = {
    data: {
      evidences: assignedEvidencesFlat,
      suggestedEvidences: isTotalEvidenceInclusion ? suggestedEvidencesFlat : [],
      additionalEvidences: isTotalEvidenceInclusion ? (additionalEvidencesQuery.data?.evidences || []) : [],
      weeks: assignedFirstPageMeta?.weeks || (isTotalEvidenceInclusion ? suggestedFirstPageMeta?.weeks : []) || (isTotalEvidenceInclusion ? additionalEvidencesQuery.data?.weeks : []) || [],
      quarters: assignedFirstPageMeta?.quarters || (isTotalEvidenceInclusion ? suggestedFirstPageMeta?.quarters : []) || (isTotalEvidenceInclusion? suggestedFirstPageMeta?.quarters : []) || [],
      months: assignedFirstPageMeta?.months || (isTotalEvidenceInclusion ? suggestedFirstPageMeta?.months : []) || (isTotalEvidenceInclusion ? additionalEvidencesQuery.data?.months : []) || [],
    },
    isLoading: assignedEvidencesQuery.isLoading || (isTotalEvidenceInclusion ? suggestedEvidencesQuery.isLoading : false) || (isTotalEvidenceInclusion? additionalEvidencesQuery.isLoading : false),
    error: assignedEvidencesQuery.error || (isTotalEvidenceInclusion ? suggestedEvidencesQuery.error : null) || (isTotalEvidenceInclusion ? additionalEvidencesQuery.error : null),
    refetch: () => {
      assignedEvidencesQuery.refetch();
      if (isTotalEvidenceInclusion) {
        suggestedEvidencesQuery.refetch();
        additionalEvidencesQuery.refetch();
      }
    }
  };


  // Activity logs query - enabled based on ideaHistoryView from context
  const activityLogsQuery = useIdeaActivityLogs(ideaId, context.ideaHistoryView);
  
  // Product status history query - for trend graph (fetches on page init)
  const productStatusHistoryQuery = useProductStatusHistory(ideaId);

  const filterByTheme = (evidenceList: any[]) => {
    if (effectiveThemeEvidenceIds && effectiveThemeEvidenceIds.length > 0) {
      return evidenceList.filter((evidence: any) => effectiveThemeEvidenceIds.includes(evidence._id));
    }
    return evidenceList;
  };
  
  // Convert active filters to string for themes API
  const activeFiltersString = useMemo(() => {
    // Sort filters by name to ensure consistent string representation
    const sortedFilters = (activeFilters || []).slice().sort((a, b) => {
      return (a.name || '').localeCompare(b.name || '');
    });
    return convertActiveFiltersToString(sortedFilters);
  }, [activeFilters]);

  // Themes query key excludes _id so applying a theme does not refetch or change key
  const activeFiltersStringForThemes = useMemo(() => {
    const withoutId = (activeFilters || []).filter((f) => f.name !== '_id');
    const sorted = withoutId.slice().sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    return convertActiveFiltersToString(sorted);
  }, [activeFilters]);

  const evidencesReady =
    !assignedEvidencesQuery.isLoading &&
    (!isTotalEvidenceInclusion || !suggestedEvidencesQuery.isLoading);

  const hasAnyEvidences =
    assignedEvidencesFlat.length > 0 ||
    (isTotalEvidenceInclusion && suggestedTotalCount > 0);

  const subIdeasQuery = useSubIdeas(ideaId);
  const hasSubIdeas = (subIdeasQuery.data?.length ?? 0) > 0;
  const subIdeasReady = subIdeasQuery.isFetched;
  // Normal themes: DB API then ML API if needed. Disabled when recalculateThemes is true.
  const themesQuery = useIdeaThemes(
    ideaId,
    activeFiltersStringForThemes,
    assignedEvidencesFlat,
    recalculateThemes,
    evidencesReady,
    filteredThemeEvidenceIds || [],
    ideaQuery.data?.themeEvidenceIds || [],
    hasAnyEvidences,
    subIdeasReady,
    suggestedApiFilters
  );

  // ── Recalculate themes ─────────────────────────────────────────────────────
  // When recalculateThemes = true: immediately call ML /sub_themes API
  // with evidenceIds, write result to cache, then reset the flag.
  const [isRecalculating, setIsRecalculating] = useState(false);
  const auth0ForRecalc = useAuth0();

  // Holds the result of the last recalculate — shown directly without touching cache
  const [recalculatedSubThemes, setRecalculatedSubThemes] = useState<any[] | null>(null);

  useEffect(() => {
    if (!recalculateThemes || !ideaId) return;
    // Shared guard: if another of the 50+ useIdeaPage instances already claimed
    // this recalculate run, skip. The Set is cleared by the effect cleanup below.
    if (_activeRecalcIds.has(ideaId)) return;
    _activeRecalcIds.add(ideaId);

    // Tie the async completion below to this effect run. If the user navigates to
    // a different idea (ideaId from useParams changes) or unmounts before the ML
    // promise settles, cleanup flips this flag so the stale resolution can't apply
    // the previous idea's themes/state to the newly opened idea.
    const recalcIdeaId = ideaId;
    let cancelled = false;

    setIsRecalculating(true);

    // Build evidenceIds from already-loaded data — no extra API call needed.
    // Use extractEvidenceIdsForMLApi so only "Strong" suggested matches are sent
    // (assigned evidences are always included regardless of match level).
    const evidenceIds = extractEvidenceIdsForMLApi(
      assignedEvidencesFlat,
      suggestedEvidencesFlat,
    );

    // Direct ML /sub_themes API call
    getSubThemes(recalcIdeaId, auth0ForRecalc, evidenceIds, activeFiltersString)
      .then((mlResponse) => {
        // If this run was superseded (idea changed) or unmounted, drop the result
        // so we don't apply the previous idea's themes to the current one.
        if (cancelled) return;
        // ML response shape: { subThemes: { subThemes: [...] } }
        const subThemes: any[] = mlResponse?.subThemes?.subThemes || [];
        // Write to React Query cache so themes persist after the flag resets.
        // Keyed by the captured idea id so it always lands on the right idea.
        queryClient.setQueryData(
          ideaKeys.themes(recalcIdeaId, activeFiltersStringForThemes, false),
          { themes: { subThemes } }
        );
        setRecalculatedSubThemes(subThemes);
        setIsThemeFound(subThemes.length > 0);
        setRecalculateThemes(false);
        // Clear any per-run evidence exclusions (e.g. set by "create idea from
        // theme") now that this recalc consumed them. Leaving them set would make
        // every later themes/recalculate merge keep excluding that evidence via
        // mergeLazySuggestedIdsForMl.
        setFilteredThemeEvidenceIds([]);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('[recalculate themes] ML API error:', err);
        setRecalculateThemes(false);
        setFilteredThemeEvidenceIds([]);
      })
      .finally(() => {
        if (cancelled) return;
        setIsRecalculating(false);
      });

    // Cleanup: React calls this when deps change (recalculateThemes → false,
    // ideaId changes) or on unmount. Flag the in-flight promise as cancelled and
    // clear the Set so the next click can fire correctly.
    return () => {
      cancelled = true;
      _activeRecalcIds.delete(recalcIdeaId);
    };
  }, [recalculateThemes, ideaId]);

  // Use recalculated themes when available, otherwise use cached themes query result
  const themesFromQuery = recalculatedSubThemes ?? (themesQuery?.data?.themes?.subThemes ?? []);

  const lastThemesRef = useRef<any[]>([]);

  useEffect(() => {
    lastThemesRef.current = [];
  }, [ideaId]);

  // themesLoading is true when:
  //  - the themes query is actively loading/fetching (covers DB + ML phases and refetches)
  //  - OR the query has no data yet and we have no cached themes (covers the one-render
  //    transition gap when evidencesReady flips true but isLoading hasn't started yet)
  // SHORT-CIRCUIT: if evidence loading is done and there are zero evidences, skip the
  // skeleton entirely so the 'no themes found' banner renders immediately.
  // isFetching covers the ML refetch that fires after an empty DB response.
  // Once the query returns data (even empty) or cached themes exist, loading stops.
  const hasCachedThemes =
    lastThemesRef.current.length > 0
    || themesFromQuery.length > 0
    || hasSubIdeas;
  const hasTopicsSectionContent = hasCachedThemes;
  // True when the DB returned no themes and we are waiting for evidences to finish
  // loading before we can trigger the ML evidence-ids → sub_themes fallback.
  // Keeps the skeleton visible so the "no themes found" banner never flashes early.
  const pendingMLFallback =
    !recalculateThemes &&
    !hasSubIdeas &&
    !evidencesReady &&
    themesQuery?.data !== undefined &&
    (themesQuery?.data?.themes?.subThemes?.length ?? 0) === 0 &&
    !themesQuery?.isFetching;
  const themesLoading = recalculateThemes
    ? true  // recalculateThemes is context state, true for the FULL duration of the ML
    // call — reset to false only inside .then()/.catch(). This avoids the
    // _activeRecalcIds race where a different useIdeaPage instance wins
    // and AiIdeaPage never sets isRecalculating=true.
    : pendingMLFallback
      ? true
      : (evidencesReady && !hasAnyEvidences)
        ? false
        : hasTopicsSectionContent
          ? false
          : (themesQuery?.isLoading || themesQuery?.isFetching) ?? false;
  if (themesFromQuery.length > 0) {
    lastThemesRef.current = themesFromQuery;
  } else {
    // If the active query has settled with an empty result (not just loading/disabled),
    // clear the ref so removed themes do not persist in the UI.
    // Clear when query settled with empty OR when evidence is gone (query disabled but data is stale)
    const queryHasSettled = !isRecalculating && themesQuery?.data !== undefined;
    if (queryHasSettled || (!hasAnyEvidences && evidencesReady)) {
      lastThemesRef.current = [];
    }
  }
  // When query is disabled (evidences refetching) or loading, keep showing last themes.
  // But if there are no evidences at all, always return empty so stale theme cards are cleared.
  const themes =
    !hasAnyEvidences && evidencesReady
      ? []
      : themesFromQuery.length > 0
        ? themesFromQuery
        : lastThemesRef.current.length > 0
          ? lastThemesRef.current
          : themesFromQuery;

  useEffect(() => {
    const subThemes = themesQuery?.data?.themes?.subThemes;
    if (!subThemes || subThemes.length === 0) {
      // Do not mark "not found" while the ML fallback is still in flight
      // (waiting for evidences or actively refetching via evidence-ids → sub_themes).
      if (pendingMLFallback || themesQuery?.isFetching) return;
      setIsThemeFound(false);
    } else {
      setIsThemeFound(true);
    }
  }, [themesQuery?.data?.themes?.subThemes, themesQuery?.isLoading, themesQuery?.isFetching, pendingMLFallback, setIsThemeFound]);

  // Mutations
  const updateIdeaMutation = useUpdateIdea(ideaId);



  const updateIdeaData = async (updatedIdea: Partial<IdeaState>) => {
    try {
      await updateIdeaMutation.mutateAsync(updatedIdea);
      return true;
    } catch (error) {
      return false;
    }
  };

  const getUnreadNotificationsCountByRequest = (requestId: string) => {
    return (
      unreadNotificationsMap.find((request) => request._id == requestId)
        ?.unreadNotificationsCount || 0
    );
  };

  const setIsCommentPresent = (comments: any[]) => {
    setComments(comments);
  };
  const isCommentPresent = comments?.length > 0;

  const toggleStarMutation = useToggleIdeaStar(ideaId);
  
  // Auto-set initial active tab when tabs load
  useEffect(() => {
    if (tabsQuery.data?.length > 0 && !activeTab) {
      setActiveTab(tabsQuery.data[0]?.label || null);
    }
  }, [tabsQuery.data, activeTab]);

  // Migration logic removed - now handled by backend

  // Debounce search query
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      context.setDebouncedSearchQuery(searchQuery);
    }, 500);
    
    return () => clearTimeout(debounceTimer);
  }, [searchQuery]);

  // Memoize filtered data to prevent infinite loops in dependent hooks
  // Return empty arrays for suggested and additional when evidenceInclusion === 'assigned'
  const filteredEvidences = useMemo(() => {
    const evidences = assignedEvidencesFlat;
    return isThemeFilterActive ? filterByTheme(evidences) : evidences;
  }, [assignedEvidencesFlat, effectiveThemeEvidenceIds, isThemeFilterActive]);
  
  const filteredMatches = useMemo(() => {
    // Return empty array if evidenceInclusion is 'assigned'
    if (evidenceInclusion === 'assigned') {
      return [];
    }
    const suggested = suggestedEvidencesFlat;
    return isThemeFilterActive ? filterByTheme(suggested) : suggested;
  }, [suggestedEvidencesFlat, effectiveThemeEvidenceIds, isThemeFilterActive, evidenceInclusion]);

  const suggestedDisplayCount = useMemo(() => {
    if (!isTotalEvidenceInclusion) {
      return 0;
    }
    if (isThemeFilterActive) {
      return filteredMatches.length;
    }
    return suggestedTotalCount;
  }, [
    isTotalEvidenceInclusion,
    isThemeFilterActive,
    filteredMatches.length,
    suggestedTotalCount,
  ]);

  const getNextEvidence = (evidenceId: string) => {
    if (!filteredMatches || filteredMatches.length === 0) return null;

    const currentIndex = filteredMatches.findIndex((evidence: any) => evidence._id === evidenceId);
    if (currentIndex === -1 || currentIndex === filteredMatches.length - 1) {
      return null;
    }

    return filteredMatches[currentIndex + 1];
  };

  // Helper function removed - now handled by backend

  // Helper function to get all filters combined (for backward compatibility)
  const getAllFilters = () => combineFilters(activeFilters, trendFilters);

  // Enhanced setters that automatically clean empty filters
  const setActiveFiltersClean = (filters: ActiveFilter[]) => {
    setActiveFilters(cleanEmptyFilters(filters));
  };

  const setTrendFiltersClean = (filters: ActiveFilter[]) => {
    setTrendFilters(cleanEmptyFilters(filters));
  };

  const suggestedRefetchAfterTextEditTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const SUGGESTED_EVIDENCES_REFETCH_AFTER_TEXT_EDIT_MS = 20_000;

  useEffect(() => {
    return () => {
      if (suggestedRefetchAfterTextEditTimeoutRef.current) {
        clearTimeout(suggestedRefetchAfterTextEditTimeoutRef.current);
      }
    };
  }, []);

  const refetchSuggestedEvidences = suggestedEvidencesQuery.refetch;
  const fetchNextSuggestedPage = suggestedEvidencesQuery.fetchNextPage;
  const fetchNextAssignedPage = assignedEvidencesQuery.fetchNextPage;

  const resolveNextEvidence = useCallback(
    async (evidenceId: string) => {
      const immediateNext = getNextEvidence(evidenceId);
      if (immediateNext) {
        return immediateNext;
      }

      const currentIndex = filteredMatches.findIndex((evidence: any) => evidence._id === evidenceId);
      if (currentIndex === -1 || !isTotalEvidenceInclusion || !hasMoreSuggested) {
        return null;
      }

      const fetchResult = await fetchNextSuggestedPage();
      const pages = fetchResult.data?.pages ?? suggestedEvidencesQuery.data?.pages;
      const flattened = pages?.flatMap((page) => page?.evidences || []) || [];
      const nextList = isThemeFilterActive ? filterByTheme(flattened) : flattened;

      return currentIndex + 1 < nextList.length ? nextList[currentIndex + 1] : null;
    },
    [
      filteredMatches,
      isTotalEvidenceInclusion,
      hasMoreSuggested,
      fetchNextSuggestedPage,
      suggestedEvidencesQuery.data?.pages,
      isThemeFilterActive,
      effectiveThemeEvidenceIds,
    ]
  );

  const scheduleRefetchSuggestedEvidencesAfterTitleOrDescriptionSave = useCallback(() => {
    if (!isTotalEvidenceInclusion) return;
    if (suggestedRefetchAfterTextEditTimeoutRef.current) {
      clearTimeout(suggestedRefetchAfterTextEditTimeoutRef.current);
    }
    suggestedRefetchAfterTextEditTimeoutRef.current = setTimeout(() => {
      suggestedRefetchAfterTextEditTimeoutRef.current = null;
      void refetchSuggestedEvidences();
    }, SUGGESTED_EVIDENCES_REFETCH_AFTER_TEXT_EDIT_MS);
  }, [isTotalEvidenceInclusion, refetchSuggestedEvidences]);

  return {
    // Server data
    idea: ideaQuery.data,
    weeks: assignedFirstPageMeta?.weeks || (isTotalEvidenceInclusion ? suggestedFirstPageMeta?.weeks : []) || [],
    quarters: assignedFirstPageMeta?.quarters || (isTotalEvidenceInclusion ? suggestedFirstPageMeta?.quarters : []) || [],
    months: assignedFirstPageMeta?.months || (isTotalEvidenceInclusion ? suggestedFirstPageMeta?.months : []) || [],
    evidences: filteredEvidences,
    assignedEvidences: assignedEvidencesFlat,
    matches: filteredMatches,
    tabs,
    suggestedSectionFiltersContext,
    themes,
    activeFiltersStringForThemes,
    activityLogs: activityLogsQuery.data || [],
    statusHistory: productStatusHistoryQuery.data || [],
    // Helper to check if there are any evidences (hoisted above for themesLoading)
    hasAnyEvidences,
    // Configuration data - replacing SyncContext
    perspectiveConfigurations: perspectiveConfigQuery.data?.perspectiveConfigurations || [],
    impactAggregationProperties: impactPropertiesQuery.data?.impactAggregationProperties || [],
    ideaInfoTabsConfig: infoTabsConfigQuery.data?.ideaInfoTabsConfig || {},
    ideaColumns: columnsConfigQuery.data?.ideaColumns || [],
    subPages: subPagesConfigQuery.data?.subPages || [],
    
    // Filtered/searched data from separate queries
    searchedEvidences: filteredEvidences || [],
    searchedSuggestedEvidences: filteredMatches || [],
    searchedAdditionalEvidences: isTotalEvidenceInclusion ? (additionalEvidencesQuery.data?.evidences || []) : [],

    // Helper fundtions
    getNextEvidence,
    resolveNextEvidence,
    getUnreadNotificationsCountByRequest,
    comments,
    setComments,
    isCommentPresent,
    setIsCommentPresent,
    // Loading states
    loading: ideaQuery.isLoading,
    evidencesLoading: assignedEvidencesQuery.isLoading,
    matchesLoading: isTotalEvidenceInclusion ? suggestedEvidencesQuery.isLoading : false,
    isFetchingNextAssignedPage: evidenceInclusion !== 'suggested' ? assignedEvidencesQuery.isFetchingNextPage : false,
    isFetchingNextSuggestedPage: isTotalEvidenceInclusion ? suggestedEvidencesQuery.isFetchingNextPage : false,
    hasMoreAssigned: evidenceInclusion !== 'suggested' ? hasMoreAssigned : false,
    hasMoreSuggested: isTotalEvidenceInclusion ? hasMoreSuggested : false,
    assignedTotalCount: isTotalEvidenceInclusion ? assignedTotalCount : 0,
    fetchNextAssignedPage: evidenceInclusion !== 'suggested' ? fetchNextAssignedPage : async () => undefined,
    suggestedTotalCount: isTotalEvidenceInclusion ? suggestedTotalCount : 0,
    suggestedDisplayCount: isTotalEvidenceInclusion ? suggestedDisplayCount : 0,
    fetchNextSuggestedPage: isTotalEvidenceInclusion ? fetchNextSuggestedPage : async () => undefined,
    additionalEvidencesLoading: isTotalEvidenceInclusion ? additionalEvidencesQuery.isLoading : false,
    tabsLoading: tabsQuery.isLoading,
    themesLoading,
    isFiltering: assignedEvidencesQuery.isLoading || (isTotalEvidenceInclusion ? suggestedEvidencesQuery.isLoading : false) || (isTotalEvidenceInclusion ? additionalEvidencesQuery.isLoading : false),
    activityLogsLoading: activityLogsQuery.isLoading,
    statusHistoryLoading: productStatusHistoryQuery.isLoading,
    
    // Configuration loading states
    perspectiveConfigLoading: perspectiveConfigQuery.isLoading,
    impactPropertiesLoading: impactPropertiesQuery.isLoading,
    infoTabsConfigLoading: infoTabsConfigQuery.isLoading,
    columnsConfigLoading: columnsConfigQuery.isLoading,
    subPagesConfigLoading: subPagesConfigQuery.isLoading,
    
    // Error states
    error: ideaQuery.error,
    evidencesError: assignedEvidencesQuery.error,
    matchesError: isTotalEvidenceInclusion ? suggestedEvidencesQuery.error : null,
    
    // Configuration error states
    perspectiveConfigError: perspectiveConfigQuery.error,
    impactPropertiesError: impactPropertiesQuery.error,
    infoTabsConfigError: infoTabsConfigQuery.error,
    columnsConfigError: columnsConfigQuery.error,
    subPagesConfigError: subPagesConfigQuery.error,
    
    // Mutations
    updateIdea: updateIdeaMutation.mutateAsync,
    toggleStar: toggleStarMutation.mutateAsync,
    isUpdating: updateIdeaMutation.isPending,
    isTogglingstar: toggleStarMutation.isPending,
    
    // Refetch functions
    refetchIdea: ideaQuery.refetch,
    refetchEvidences: assignedEvidencesQuery.refetch,
    refetchMatches: isTotalEvidenceInclusion ? suggestedEvidencesQuery.refetch : () => Promise.resolve(),
    refetchSuggestedEvidences,
    scheduleRefetchSuggestedEvidencesAfterTitleOrDescriptionSave,
    refetchFilteredEvidences: () => {
      if (isTotalEvidenceInclusion) {
        suggestedEvidencesQuery.refetch();
      }
    },
    refetchActivityLogs: activityLogsQuery.refetch,
    
    // All context state and actions
    ...context,
    // Override with derived value when multiple theme filters are applied
    themeEvidenceIds: effectiveThemeEvidenceIds,

    // Filter helper functions
    getAllFilters,
    setActiveFiltersClean,
    setTrendFiltersClean,
    
    // Derived data that components might need
    filteredEvidences,
    filteredMatches,
  };
};

// Specialized hooks for specific use cases
export const useIdeaSearch = () => {
  const { 
    searchQuery, 
    setSearchQuery, 
    debouncedSearchQuery,
    isSearching,
    setIsSearching,
    clearSearch,
    searchedEvidences,
    searchedSuggestedEvidences,
    searchedAdditionalEvidences,
    isFiltering
  } = useIdeaPage();
  
  const searchEvidence = (query: string) => {
    setSearchQuery(query);
    setIsSearching(query.trim() !== '');
  };
  
  return {
    searchQuery,
    setSearchQuery,
    debouncedSearchQuery,
    isSearching,
    setIsSearching,
    isFiltering,
    searchEvidence,
    clearSearch,
    searchedEvidences,
    searchedSuggestedEvidences,
    searchedAdditionalEvidences,
  };
};

export const useIdeaSort = () => {
  const { 
    sortConfig, 
    setSortConfig, 
    clearSort,
    refetchFilteredEvidences 
  } = useIdeaPage();
  
  const sortEvidence = (field: string, direction?: 'asc' | 'desc') => {
    const newDirection = direction || (sortConfig.field === field && sortConfig.direction === 'asc' ? 'desc' : 'asc');
    setSortConfig({ field, direction: newDirection });
  };
  
  return {
    sortConfig,
    sortEvidence,
    clearSort,
  };
};

export const useIdeaPageTabs = (): any => {
  const { 
    tabs, 
    activeTab, 
    setActiveTab, 
    tabsLoading,
    searchedEvidences,
    searchedSuggestedEvidences,
    evidences,
    matches,
    themeEvidenceIds,
    isSearching,
    debouncedSearchQuery,
    activeFilters,
    trendFilters,
    sortConfig,
    evidenceInclusion,
    filteredMatches,
    evidencesLoading,
    matchesLoading,
    additionalEvidencesLoading,
    suggestedTotalCount,
    suggestedDisplayCount,
    hasMoreSuggested,
    hasMoreAssigned,
    isFetchingNextSuggestedPage,
    isFetchingNextAssignedPage,
    fetchNextSuggestedPage,
    fetchNextAssignedPage,
    suggestedSectionFiltersContext,
  } = useIdeaPage();

  const {
    apiMergeEnabled,
    apiMergedSectionFilterFieldPaths,
  } = suggestedSectionFiltersContext;

  const evidenceSectionLoading = useMemo(
    () => ({
      assigned: evidencesLoading,
      suggested: matchesLoading,
      additional: additionalEvidencesLoading,
    }),
    [evidencesLoading, matchesLoading, additionalEvidencesLoading]
  );
  const { id: ideaId } = useParams<{ id: string }>();

  const tabEvidenceTotalsQuery = useTabEvidenceTotals(
    ideaId,
    activeFilters,
    trendFilters,
    sortConfig.field && sortConfig.direction
      ? { field: sortConfig.field, direction: sortConfig.direction }
      : undefined,
    evidenceInclusion
  );

  const activeTabSectionCounts = useMemo(() => {
    const activeTabLabel = activeTab || '';
    return tabEvidenceTotalsQuery.data?.countsByTab?.[activeTabLabel]?.sections || [];
  }, [tabEvidenceTotalsQuery.data?.countsByTab, activeTab]);

  const processedEvidences = useMemo(() => {
    const assignedEvidences = evidences || [];
    const suggestedEvidences = filteredMatches || [];
    return {
      assigned: assignedEvidences,
      suggested: suggestedEvidences,
      all: [...assignedEvidences, ...suggestedEvidences],
    };
  }, [evidences, filteredMatches]);
  
  // Helper function to get nested value from object
  const getNestedValue = (obj: any, path: string) => {
    if (!path) return undefined;
    return path.split('.').reduce((current, key) => current?.[key], obj);
  };

  // Helper function to filter evidences by ActiveFilter format
  const filterEvidencesByFilters = (evidences: any[], filters: any[]) => {
    if (!filters || filters.length === 0) return evidences;
    
    return evidences.filter((evidence: any) => {
      return filters.every((filter: any) => {
        const fieldPath = filter.dbFieldPath || filter.name;
        if (!fieldPath) return true; // Skip filters without a valid field path
        const value = getNestedValue(evidence, fieldPath);
        const filterType = filter.type;
        const operator = filter.operator || 'in';
        
        // Handle checkbox/fetchableCheckbox filters
        if (filterType === 'checkbox' || filterType === 'fetchableCheckbox') {
          if (operator === 'in') {
            return doesCheckboxInFilterMatch(filter.selected, value, { ideaId });
          }
          if (operator === 'contains') {
            const selected = filter.selected || [];
            if (Array.isArray(value)) {
              return selected.some((sel: any) => value.includes(sel));
            }
            return selected.includes(value);
          }
          if (operator === 'has-any') {
            const selected = filter.selected || [];
            if (Array.isArray(value)) {
              return selected.some((sel: any) => value.includes(sel));
            }
            return false;
          }
        }
        
        // Handle number filters
        if (filterType === 'number') {
          const queryValue = filter.query;
          if (typeof value !== 'number' || queryValue === undefined) return false;
          
          switch (operator) {
          case 'greater_than':
            return value > queryValue;
          case 'lower_than':
            return value < queryValue;
          case 'equal':
            return value === queryValue;
          case 'not_equal':
            return value !== queryValue;
          default:
            return false;
          }
        }
        
        // Handle boolean filters
        if (filterType === 'boolean') {
          const expectedValue = operator === 'true' || filter.selected === true;
          return Boolean(value) === expectedValue;
        }
        
        // Handle _id filters
        if (filterType === '_id') {
          const selected = filter.selected || [];
          if (operator === 'in') {
            return selected.includes(value?.toString());
          }
          if (operator === 'contains' && Array.isArray(value)) {
            return selected.some((sel: any) => value.map((v: any) => v?.toString()).includes(sel));
          }
        }
        
        // Handle date filters (basic support - full date range logic would be more complex)
        if (filterType === 'date') {
          // For now, just check if field exists when operator is set
          // Full date range matching would require date parsing
          if (operator && value) {
            return true; // Simplified - assumes date exists
          }
          if (filter.selected && Array.isArray(filter.selected) && filter.selected.length === 2) {
            const [startDate, endDate] = filter.selected;
            const evidenceDate = new Date(value);
            const start = new Date(startDate);
            const end = new Date(endDate);
            return evidenceDate >= start && evidenceDate <= end;
          }
        }
        
        return true;
      });
    });
  };

  // Filter evidences by tab filters with dataSource selection and sub_sections support
  const getFilteredEvidencesForTab = (tab: any) => {
    if (!tab?.sections) return [];

    const tabApiMergeEnabled = suggestedSectionFiltersContext.apiMergeEnabled;
    const tabApiMergedSectionFilterFieldPaths =
      suggestedSectionFiltersContext.apiMergedSectionFilterFieldPaths;
    
    return tab.sections.map((section: any) => {
      // Determine the appropriate source based on dataSource field
      const getEvidenceSource = (section: any) => {
        const dataSource = section.dataSource;
        
        // Support new dataSource field
        if (dataSource === 'assigned') {
          return processedEvidences.assigned;
        }
        if (dataSource === 'suggested') {
          return processedEvidences.suggested;
        }
        if (dataSource === 'additional') {
          // Additional evidences come from search results
          return searchedEvidences || [];
        }
        
        // Fallback: support legacy conditions format for backward compatibility
        if (section.conditions) {
          const hasMatchCondition = section.conditions.some((c: any) => c.field === 'match');
          if (hasMatchCondition) {
            const hasAssignedMatch = section.conditions.some((c: any) => c.field === 'match' && c.$isNull === 1);
            const hasSuggestedMatch = section.conditions.some((c: any) => c.field === 'match' && c.$isNull === 0);
            
            if (hasAssignedMatch && !hasSuggestedMatch) {
              return processedEvidences.assigned;
            }
            if (hasSuggestedMatch && !hasAssignedMatch) {
              return processedEvidences.suggested;
            }
          }
        }
        
        // Default to all evidences
        return processedEvidences.all;
      };
      
      const sourceEvidences = getEvidenceSource(section);
      
      const clientSectionFilters = getClientSectionFiltersToApply(
        section,
        tabApiMergeEnabled,
        tabApiMergedSectionFilterFieldPaths
      );

      let filteredEvidences = sourceEvidences;
      if (clientSectionFilters.length > 0) {
        filteredEvidences = filterEvidencesByFilters(sourceEvidences, clientSectionFilters);
      } else if (section.conditions && Array.isArray(section.conditions) && section.conditions.length > 0) {
        filteredEvidences = filterEvidencesByConditions(sourceEvidences, section.conditions);
      }
      
      // Process sub_sections if they exist
      let mainSectionEvidences = [...filteredEvidences];
      const subSectionData: any[] = [];
      
      if (section.sub_sections && section.sub_sections.length > 0) {
        section.sub_sections.forEach((subSection: any) => {
          // Filter evidences that match sub_section filters/conditions
          let subSectionEvidences = filteredEvidences;
          if (subSection.filters && Array.isArray(subSection.filters) && subSection.filters.length > 0) {
            subSectionEvidences = filterEvidencesByFilters(filteredEvidences, subSection.filters);
          } else if (subSection.conditions && Array.isArray(subSection.conditions) && subSection.conditions.length > 0) {
            subSectionEvidences = filterEvidencesByConditions(filteredEvidences, subSection.conditions);
          }
          
          if (subSectionEvidences.length > 0) {
            subSectionData.push({
              ...subSection,
              evidences: subSectionEvidences
            });
            
            // Remove sub_section evidences from the main section (they'll be shown separately)
            mainSectionEvidences = mainSectionEvidences.filter(evidence => 
              !subSectionEvidences.some((subEvidence: any) => subEvidence._id === evidence._id)
            );
          }
        });
      }
      
      const loadedSectionCount =
        mainSectionEvidences.length +
        subSectionData.reduce((sum, subSection) => sum + subSection.evidences.length, 0);

      const tabTotalsSectionCount = resolveSectionCountFromTabTotals(
        activeTabSectionCounts,
        section
      );

      const sectionDisplayCount = tabTotalsSectionCount ?? loadedSectionCount;

      return {
        ...section,
        evidences: mainSectionEvidences,
        sub_sections: subSectionData,
        sectionLoading: isEvidenceSectionLoading(section, evidenceSectionLoading),
        sectionDisplayCount,
        isSuggestedSection: isSuggestedSection(section),
      };
    });
  };
  
  // Keep legacy function for backward compatibility
  const filterEvidencesByConditions = (evidences: any[], conditions: any[]) => {
    return evidences.filter((evidence: any) => {
      return conditions?.every((condition: any) => {
        const { field, $eq, $isNull, $neq } = condition;
        const fieldValue = getNestedValue(evidence, field);
        
        if ($eq !== undefined) {
          return fieldValue === $eq;
        }
        
        if ($neq !== undefined) {
          return fieldValue !== $neq;
        }
        
        if ($isNull !== undefined) {
          if ($isNull === 0) {
            return fieldValue != null && fieldValue !== undefined;
          } else if ($isNull === 1) {
            return fieldValue == null || fieldValue === undefined;
          }
        }
        
        return true;
      });
    });
  };
  
  const currentTabData = tabs.find((tab: any) => tab.label === activeTab);
  const filteredEvidencesForActiveTab = currentTabData ? getFilteredEvidencesForTab(currentTabData) : [];
  
  // Calculate evidence counts for all tabs
  const tabsWithCounts = useMemo(() => {
    const countsByTab = tabEvidenceTotalsQuery.data?.countsByTab || {};
    const isTabTotalsLoading =
      tabEvidenceTotalsQuery.isLoading
      || tabEvidenceTotalsQuery.isFetching;

    return tabs.map((tab: any) => {
      const tabLabel = tab.label || tab.name || '';
      const tabTotal = countsByTab[tabLabel];

      return {
        ...tab,
        evidenceCount: tabTotal?.totalCount ?? 0,
        isCountLoading: tabsLoading || isTabTotalsLoading,
      };
    });
  }, [
    tabs,
    tabsLoading,
    tabEvidenceTotalsQuery.data,
    tabEvidenceTotalsQuery.isLoading,
    tabEvidenceTotalsQuery.isFetching,
  ]);

  return {
    tabs: tabsWithCounts,
    activeTab,
    setActiveTab,
    tabsLoading,
    currentTabData,
    filteredEvidencesForActiveTab,
    suggestedTotalCount,
    suggestedDisplayCount,
    hasMoreSuggested,
    hasMoreAssigned,
    isFetchingNextSuggestedPage,
    isFetchingNextAssignedPage,
    fetchNextSuggestedPage,
    fetchNextAssignedPage,
  };
};

export const usePerspectives = (): any => {
  const {
    chosenKey,
    setChosenKey,
    perspectiveData,
    perspectiveDataLoading,
  } = useIdeaPageContext();

  const currentPerspectiveData = chosenKey ? perspectiveData[chosenKey] || {} : {};

  return {
    perspectiveData: chosenKey ? { [chosenKey]: currentPerspectiveData } : {},
    perspectiveDataLoading,
    perspectiveDataError: null,
    chosenKey,
    setChosenKey,
  };
};

/**
 * Custom hook to fetch trend counts for a custom created-at date range.
 * Triggers automatically when a CreatedAt date filter exists in trendFilters.
 * Returns the raw backend response:
 * {
 *   success: boolean,
 *   fieldPath: 'week' | 'month' | 'quarter',
 *   trendRange: 'Custom',
 *   counts: Array<{ value: string, evidenceCount?: number, suggestedCount?: number, total?: number }>,
 *   rangeValues: string[],
 *   message?: string
 * }
 */
export const useCustomTrendRange = () => {
  const { id: ideaId } = useParams<{ id: string }>();
  const auth0 = useAuth0();
  const {
    activeFilters,
    trendFilters,
  } = useIdeaPage();

  // Combine business filters with date/time filters
  const allFilters = useMemo(() => combineFilters(activeFilters, trendFilters), [activeFilters, trendFilters]);
  const customTrendFiltersKey = useMemo(
    () => buildPerspectiveFiltersCacheKey(activeFilters, trendFilters),
    [activeFilters, trendFilters]
  );

  // Detect presence of a CreatedAt date filter with a selected range
  const hasCustomCreatedAtRange = useMemo(() => {
    if (!Array.isArray(trendFilters)) return false;
    const fByName = trendFilters.find((f: any) => (f?.name || '').toString().toLowerCase() === 'createdat' && f?.type === 'date' && f?.selected);
    const fByParent = trendFilters.find((f: any) => (f?.parent || '').toString().toLowerCase() === 'createdat' && f?.type === 'date' && f?.selected);
    return Boolean(fByName || fByParent);
  }, [trendFilters]);

  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<any>(null);

  const auth0Ref = useRef(auth0);
  auth0Ref.current = auth0;
  const allFiltersRef = useRef(allFilters);
  allFiltersRef.current = allFilters;

  const refetch = useCallback(async () => {
    if (!ideaId || !hasCustomCreatedAtRange) return;
    setLoading(true);
    setError(null);
    try {
      const res = await getCustomTimeRangeCount(ideaId, allFiltersRef.current, auth0Ref.current);
      setData(res);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [ideaId, hasCustomCreatedAtRange]);

  useEffect(() => {
    if (hasCustomCreatedAtRange) {
      refetch();
    } else {
      setData(null);
      setError(null);
      setLoading(false);
    }
  }, [hasCustomCreatedAtRange, customTrendFiltersKey, refetch]);

  return {
    customTrendData: data,
    customTrendLoading: loading,
    customTrendError: error,
    refetchCustomTrendRange: refetch,
  };
};
